<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PRIMEVUE
import { default as PInputGroup } from 'primevue/inputgroup'
import { default as PTextarea } from 'primevue/textarea'
import { default as PInputGroupAddon } from 'primevue/inputgroupaddon'
import { default as PButton } from 'primevue/button'
import draggable from 'vuedraggable'

// Custom components and types
import { QuizQuestionAnswerSequencing } from '@/core/domain/quiz/QuizQuestionAnswer.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'

// VUE, VUE ROUTER, ETC...
import { type ModelRef } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const answers: ModelRef<QuizQuestionAnswerSequencing[]> = defineModel('answers', {
  required: true
})

const props = defineProps<{
  canModifyStructure?: boolean
  canModifyContent?: boolean
}>()

function addQuestion() {
  answers.value.push(
    QuizQuestionAnswerSequencing.create({
      type: LearningQuizQuestionType.Values.SEQUENCING,
      data: {
        type: LearningQuizQuestionType.Values.SEQUENCING,
        value: ''
      },
      order: answers.value.length + 1
    })
  )
}

const dragOptions = {
  animation: 150,
  group: 'group1',
  disabled: false,
  ghostClass: 'ghost'
}

function deleteAnswer(index: number) {
  answers.value.splice(index, 1)
  updateAnswers()
}

function updateAnswers() {
  answers.value.forEach((answer, i) => {
    answer.order = i + 1
  })
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <draggable
      v-model="answers"
      handle=".handle"
      v-bind="dragOptions"
      item-key="order"
      :component-data="{ tag: 'div', name: 'flip-list', type: 'transition' }"
      class="list-group flex flex-col gap-1"
      :disabled="!props.canModifyStructure"
      @end="updateAnswers"
    >
      <template
        #item="{ element, index }: { element: QuizQuestionAnswerSequencing; index: number }"
      >
        <div class="flex w-full items-center gap-2">
          <FontAwesomeIcon icon="grip-vertical" class="handle cursor-move" />
          <p-input-group>
            <p-input-group-addon>
              <label>{{ index + 1 }}</label>
            </p-input-group-addon>
            <p-textarea
              v-model="element.data.value"
              class="flex-1"
              placeholder="Texte de la réponse"
              :disabled="!props.canModifyContent"
            />
          </p-input-group>
          <p-button
            @click="deleteAnswer(index)"
            icon="pi pi-trash"
            text
            severity="danger"
            :disabled="!props.canModifyStructure"
          />
        </div>
      </template>
    </draggable>

    <!-- Messages d'information -->
    <div
      v-if="!props.canModifyStructure && props.canModifyContent"
      class="mt-2 text-xs text-amber-600"
    >
      <i class="pi pi-info-circle mr-1"></i>
      Impossible d'ajouter/supprimer/réorganiser des réponses sur cette version
    </div>

    <div v-if="!props.canModifyContent" class="mt-2 text-xs text-red-600">
      <i class="pi pi-lock mr-1"></i>
      Version verrouillée - aucune modification autorisée
    </div>
  </div>

  <p-button
    label="Ajouter une réponse"
    icon="pi pi-plus"
    class="mt-4"
    size="small"
    outlined
    :disabled="!props.canModifyStructure"
    @click="addQuestion"
  />
</template>

<style>
.button {
  margin-top: 35px;
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}
</style>
